import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { BILLING_LIST_PAGE_SIZE } from "../constants/pagination";
import {
  BillingAvailability,
  OrganizationContractsDocument,
  OrganizationContractsQuery,
  OrganizationContractsQueryVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";
import { captureException } from "../lib/errorHandling";
import { borderRadius, color, shadows, space } from "../theme/designTokens";
import { LoadStatus } from "../types/loadStatus";
import { CursorPagination } from "./CursorPagination";

type ContractsState =
  | { status: LoadStatus.Loading | LoadStatus.Error }
  | { status: LoadStatus.Ready; contracts: OrganizationContractsQuery["organizationContracts"] };

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));

type OrganizationContractsSectionProps = {
  organizationId?: string;
};

export const OrganizationContractsSection = ({ organizationId }: OrganizationContractsSectionProps) => {
  const translate = useTranslate();
  const [state, setState] = useState<ContractsState>({ status: LoadStatus.Loading });
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [reload, setReload] = useState(0);
  const after = cursors[cursors.length - 1];

  useEffect(() => {
    let active = true;
    const load = async () => {
      setState({ status: LoadStatus.Loading });
      try {
        const result = await client.query<OrganizationContractsQuery, OrganizationContractsQueryVariables>({
          query: OrganizationContractsDocument,
          variables: organizationId ? { after, organizationId } : { after },
          fetchPolicy: "no-cache",
          context: { queryDeduplication: false },
        });
        if (active) {
          const contracts = result.data.organizationContracts;
          if (
            after !== null &&
            contracts.availability === BillingAvailability.Available &&
            contracts.items.length === 0
          ) {
            setCursors(value => value.slice(0, -1));
            return;
          }
          setState({ status: LoadStatus.Ready, contracts });
        }
      } catch (error) {
        captureException(error, "billing/contracts: load organisation contracts");
        if (active) setState({ status: LoadStatus.Error });
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [after, organizationId, reload]);

  const contracts = state.status === LoadStatus.Ready ? state.contracts : null;
  return (
    <Box component="section" aria-labelledby="contracts-heading" data-testid="organization-contracts" mb={space.sm2}>
      <Paper
        variant="outlined"
        sx={{
          borderColor: color.panelBorder,
          borderRadius: borderRadius.panel,
          boxShadow: shadows.card,
          overflow: "hidden",
        }}
      >
        <Stack gap={space.sm} p={space.sm2}>
          <Typography id="contracts-heading" variant="h6">
            {translate("contracts.title")}
          </Typography>
          {state.status === LoadStatus.Loading && (
            <Box role="status" aria-label={translate("contracts.loading")}>
              <Skeleton width="60%" />
              <Skeleton height={56} />
            </Box>
          )}
          {state.status === LoadStatus.Error && (
            <Alert
              severity="error"
              action={<Button onClick={() => setReload(value => value + 1)}>{translate("billing.retry")}</Button>}
            >
              {translate("contracts.loadError")}
            </Alert>
          )}
          {contracts && (
            <>
              <Typography color="text.secondary">
                {translate("contracts.description", {
                  organization: contracts.organizationName || translate("account.notAvailable"),
                })}
              </Typography>
              {contracts.availability === BillingAvailability.Disabled ? (
                <Alert severity="info">{translate("billing.accessDisabled")}</Alert>
              ) : contracts.availability === BillingAvailability.NotConfigured ? (
                <Alert severity="info">{translate("contracts.notConfigured")}</Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {translate("contracts.snapshot")}
                  </Typography>
                  {contracts.items.length === 0 ? (
                    <Typography>{translate("contracts.empty")}</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small" aria-label={translate("contracts.title")}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{translate("contracts.name")}</TableCell>
                            <TableCell>{translate("contracts.product")}</TableCell>
                            <TableCell>{translate("contracts.status")}</TableCell>
                            <TableCell>{translate("contracts.dates")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {contracts.items.map(item => (
                            <TableRow key={item.id}>
                              <TableCell component="th" scope="row">
                                {item.name}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{item.productName || item.product}</Typography>
                                {item.productName && (
                                  <Typography variant="caption" component="div" color="text.secondary">
                                    {translate("contracts.family", { value: item.product })}
                                  </Typography>
                                )}
                                {item.plan && (
                                  <Typography variant="caption" component="div">
                                    {translate("contracts.plan", {
                                      value: translate(`contracts.plans.${item.plan}`, { _: item.plan }),
                                    })}
                                  </Typography>
                                )}
                                {item.flavour && (
                                  <Typography variant="caption" component="div">
                                    {translate("contracts.flavour", { value: item.flavour.toUpperCase() })}
                                  </Typography>
                                )}
                                {item.sponsoringOrganization && (
                                  <Typography variant="caption" component="div">
                                    {translate("contracts.sponsor", { value: item.sponsoringOrganization })}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {translate(`contracts.statuses.${item.status}`, { _: item.status })}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">
                                  {translate("contracts.startDate", { date: formatDate(item.startDate) })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.endDate
                                    ? translate("contracts.endDate", { date: formatDate(item.endDate) })
                                    : translate("contracts.noEndDate")}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={space.xs}
                  >
                    <Button
                      startIcon={<RefreshOutlinedIcon />}
                      onClick={() => {
                        setCursors([null]);
                        setReload(value => value + 1);
                      }}
                    >
                      {translate("billing.refresh")}
                    </Button>
                    <CursorPagination
                      page={cursors.length}
                      perPage={BILLING_LIST_PAGE_SIZE}
                      itemCount={contracts.items.length}
                      hasNextPage={Boolean(contracts.nextCursor)}
                      resource="OrganizationContract"
                      onPageChange={page => {
                        if (page >= 1 && page < cursors.length) setCursors(value => value.slice(0, page));
                        const nextCursor = contracts.nextCursor;
                        if (page === cursors.length + 1 && nextCursor) {
                          setCursors(value => [...value, nextCursor]);
                        }
                      }}
                    />
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
